import { Link, useParams } from "wouter";
import { SimpleHeader, SimpleFooter, AnimatedBlock } from "@/components/simple-page-chrome";
import { GLOSARIO_TERMS } from "@/content/glosario";
import NotFound from "@/pages/not-found";

export function GlosarioIndexPage() {
  const sorted = [...GLOSARIO_TERMS].sort((a, b) => a.term.localeCompare(b.term, "es"));

  return (
    <div className="min-h-screen bg-background">
      <SimpleHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <AnimatedBlock>
          <p className="text-sm font-medium text-primary tracking-wider uppercase mb-3">Glosario</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Glosario de términos de la NOM-035
          </h1>
          <p className="text-muted-foreground mb-12 max-w-2xl">
            Los conceptos clave de la NOM-035-STPS explicados en lenguaje claro, para que tu empresa entienda exactamente qué exige la norma.
          </p>
        </AnimatedBlock>

        <ul className="divide-y divide-border">
          {sorted.map((t, i) => (
            <AnimatedBlock key={t.slug} delay={Math.min(i * 0.02, 0.3)}>
              <li className="py-4">
                <Link
                  href={`/glosario/${t.slug}`}
                  className="font-serif font-semibold text-foreground hover:text-primary transition-colors"
                  data-testid={`link-glosario-${t.slug}`}
                >
                  {t.term}
                </Link>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed line-clamp-2">{t.definition}</p>
              </li>
            </AnimatedBlock>
          ))}
        </ul>
      </main>
      <SimpleFooter />
    </div>
  );
}

export function GlosarioTermPage() {
  const { slug } = useParams<{ slug: string }>();
  const term = GLOSARIO_TERMS.find((t) => t.slug === slug);

  if (!term) {
    return <NotFound />;
  }

  const related = GLOSARIO_TERMS.filter((t) => term.relatedSlugs?.includes(t.slug));

  const definedTermJsonLd = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: term.term,
    description: term.definition,
    inDefinedTermSet: {
      "@type": "DefinedTermSet",
      name: "Glosario NOM-035 — MAPA35",
      url: "https://www.mapa35.com/glosario",
    },
  };

  return (
    <div className="min-h-screen bg-background">
      <SimpleHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <AnimatedBlock>
          <Link href="/glosario" className="text-sm text-primary hover:underline" data-testid="link-back-glosario">
            ← Glosario NOM-035
          </Link>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mt-4 mb-6">
            {term.term}
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
            {term.definition}
          </p>
        </AnimatedBlock>

        {related.length > 0 && (
          <AnimatedBlock delay={0.1}>
            <div className="mt-12 pt-8 border-t">
              <p className="text-sm font-medium text-foreground mb-4">Términos relacionados</p>
              <div className="flex flex-wrap gap-4">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    href={`/glosario/${r.slug}`}
                    className="text-sm text-primary hover:underline"
                    data-testid={`link-related-${r.slug}`}
                  >
                    {r.term} →
                  </Link>
                ))}
              </div>
            </div>
          </AnimatedBlock>
        )}

        <AnimatedBlock delay={0.15}>
          <div className="mt-12 pt-8 border-t">
            <Link href="/faq" className="text-sm text-primary hover:underline" data-testid="link-term-to-faq">
              Ver preguntas frecuentes sobre la NOM-035 →
            </Link>
          </div>
        </AnimatedBlock>
      </main>
      <SimpleFooter />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(definedTermJsonLd) }}
      />
    </div>
  );
}
